import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { EXPLORER_URL } from '../lib/network';
import { Blocks, Coins, GitBranch, Layers, Lock } from 'lucide-react';
import type { MemberLedger, OnChainMemberView, OnChainSeat } from '../lib/lookup-types';
import { formatDate, formatToken, shortenAddress } from '../lib/format';
import { atomicToDecimal } from '../state/AppDataContext';
import { useWallet } from '../state/WalletContext';
import { VAULT_ABI, resolveGovernanceAddresses, sendGovernanceCall } from '../lib/governance';
import { compareSlots, slotLabel } from '../lib/slots';
import { burnTierLines, useBurnTiers } from './TokenLiquidityInfo';
import type { Address } from 'viem';

/*
 * Contract V2 (docs/V2_DESIGN.md): what the blockchain actually recorded for one ID - its slots
 * and every seat in them, the seats it took in other matrices, every routing decision with the
 * candidate path, every reward the vault paid it, the sponsor income its joining paid out, escrow
 * and claims. Shown identically in the member panel, the public lookup and the admin explorer
 * (owner instruction 2026-09-24 (a)). Nothing here is a projection.
 */

const KIND_LABEL: Record<string, string> = {
  sponsor_one: 'Sponsor-1 routing', sponsor_two: 'Sponsor-2 routing', slot_owner: 'Slot owner', upgrade_hold: 'Upgrade holding',
  upgrade_complete: 'Upgrade completion', token_liquidity: 'Token Liquidity', leaders_club: 'Leaders Club', downline_route: 'Downline routing',
  recycle: 'Recycle', treasury_fallback: 'Treasury fallback', forfeited: 'Forfeited holding (dormancy)',
};
const ROUTING_LABEL: Record<string, string> = {
  sponsor_one: 'Sponsor-1', sponsor_two: 'Sponsor-2', downline_first: 'First direct downline', downline_second: 'Second direct downline', upgrade: 'Upgrade', recycle: 'Recycle payment',
};
const btc = (atomic: string) => formatToken(atomicToDecimal(atomic));
/** Owner 2026-10-09: the contract routes cycle 1 to the first direct downline and cycle 2+ to the second one. */
const kindLabel = (kind: string, cycle: number) => (kind === 'downline_route' ? (cycle >= 2 ? '2nd Downline routing' : '1st Downline routing') : KIND_LABEL[kind] ?? kind);
const tx = (hash: string) => <a href={`${EXPLORER_URL}/tx/${hash}`} target="_blank" rel="noreferrer" className="tx-link">{hash.slice(0, 10)}…</a>;

function SeatTable({ seats, cycle }: { seats: OnChainSeat[]; cycle: number }) {
  const rows = Array.from({ length: 12 }, (_, index) => seats.find((seat) => seat.cycle === cycle && seat.position === index + 1 && seat.kind !== 'treasury_fallback' && seat.kind !== 'forfeited'));
  const forfeited = seats.filter((seat) => seat.cycle === cycle && seat.kind === 'forfeited');
  const fallbacks = seats.filter((seat) => seat.cycle === cycle && seat.kind === 'treasury_fallback');
  return (
    <>
    {forfeited.length ? <p className="empty-copy">Dormancy forfeiture (rule 34): the holding of {forfeited.map((seat) => `P${seat.position} (${seat.occupantBdId}, ${btc(seat.amountAtomic)})`).join(' + ')} went to the treasury; {forfeited.length === 1 ? 'that position is' : 'those positions are'} blank again and can be refilled.</p> : null}
    <table className="pt-table onchain-seats">
      <thead><tr><th>Pos</th><th>Occupant</th><th>Rule</th><th>Value</th><th>Tx</th></tr></thead>
      <tbody>
        {rows.map((seat, index) => (
          <tr key={index} className={seat ? '' : 'open'}>
            <td>P{index + 1}</td>
            <td>{seat ? seat.occupantBdId : <span className="muted">open</span>}</td>
            <td>{seat ? kindLabel(seat.kind, seat.cycle) : ''}{seat && fallbacks.some((f) => f.position === seat.position) ? ' → Treasury' : ''}</td>
            <td>{seat ? btc(seat.amountAtomic) : ''}</td>
            <td>{seat ? tx(seat.txHash) : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
}

/** Fetches the on-chain record for one ID and renders it; used by the member panel, lookup and admin. */
export function OnChainMatrixSection({ bdId, compact = false }: { bdId: string; compact?: boolean }) {
  const [data, setData] = useState<{ view: OnChainMemberView | null; ledger: MemberLedger | null } | null>(null);
  const [failed, setFailed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const burnTiers = useBurnTiers();
  useEffect(() => {
    let cancelled = false;
    setData(null); setFailed(false);
    api<{ view: OnChainMemberView | null; ledger: MemberLedger | null }>(`/api/public/onchain/${encodeURIComponent(bdId)}`)
      .then((result) => { if (!cancelled) setData(result); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [bdId, refresh]);
  if (failed) return <section className="panel onchain-card"><p className="empty-copy">On-chain record could not be loaded.</p></section>;
  if (!data || !data.view) return <section className="panel onchain-card"><p className="empty-copy">Reading the on-chain matrix record…</p></section>;
  return (
    <>
      <OnChainMatrixCard view={data.view} compact={compact} />
      {data.ledger ? (
        <section className="panel onchain-ledger">
          <div className="panel-heading"><div><span className="eyebrow">VAULT LEDGER (LIVE)</span><h2>ID-wise sub-ledger of {bdId}</h2></div></div>
          <dl className="primary-token-grid">
            <div><dt>Cumulative earnings</dt><dd>{btc(data.ledger.cumulativeEarningsAtomic)}</dd></div>
            <div><dt>Claimable now</dt><dd>{btc(data.ledger.claimableAtomic)}<ClaimButton bdId={bdId} idWallet={data.view.wallet ?? null} claimableAtomic={data.ledger.claimableAtomic} onDone={() => setRefresh((value) => value + 1)} /></dd></div>
            <div><dt>Upgrade escrow held</dt><dd>{btc(data.ledger.upgradeEscrowAtomic)}</dd></div>
            <div><dt>Token Liquidity share</dt><dd>{btc(data.ledger.liquidityShareAtomic)}</dd></div>
            <div><dt>Parked in Treasury Reserve (dormancy)</dt><dd>{btc(data.ledger.reserveAtomic)}</dd></div>
            <div><dt>Live liquidation value</dt><dd>{data.ledger.liquidationQuote ? `${btc(data.ledger.liquidationQuote.payoutAtomic)} (pool ${btc(data.ledger.liquidationQuote.poolAtomic)}, weight ${data.ledger.liquidationQuote.ratioBps / 100}% of ${Number(data.ledger.liquidationQuote.weightedBps) / 100}%)` : '—'}</dd></div>
            <div><dt>Last activity / dormant since</dt><dd>{data.ledger.dormancy?.lastActivityAt ? formatDate(data.ledger.dormancy.lastActivityAt) : '—'} / {data.ledger.dormancy?.dormantSince ? formatDate(data.ledger.dormancy.dormantSince) : 'active'}</dd></div>
          </dl>
          {data.view.liquidation ? <p className="empty-copy">Liquidated: redemption #{data.view.liquidation.redemptionId} paid {btc(data.view.liquidation.payoutAtomic)} on {formatDate(data.view.liquidation.at)} · {tx(data.view.liquidation.txHash)}</p> : null}
          {data.view.dormancy.length ? <p className="empty-copy">Dormancy: {data.view.dormancy.map((row) => `${row.kind} ${btc(row.amountAtomic)} (${formatDate(row.at)})`).join(' · ')}</p> : null}
          <p className="empty-copy">Burn tiers (rule 19 Q9 + rule 40, read from the token contract; the threshold follows the token's issue era): {burnTiers ? burnTierLines(burnTiers, btc).join(' — ') : '—'}.</p>
        </section>
      ) : null}
    </>
  );
}

export function OnChainMatrixCard({ view, compact = false }: { view: OnChainMemberView; compact?: boolean }) {
  // Progression order (Slot ¼, Slot ½, 1 .. 16); open on the first REAL slot - a virtual ½ only seats lower-era IDs.
  const slots = [...view.slots].sort((a, b) => compareSlots(a.slot, b.slot));
  const [openSlot, setOpenSlot] = useState<number | null>((slots.find((item) => !item.virtual) ?? slots[0])?.slot ?? null);
  const [showRouting, setShowRouting] = useState(false);
  const slot = slots.find((item) => item.slot === openSlot) ?? null;
  const cycles = slot ? Array.from(new Set([...slot.seats.map((seat) => seat.cycle), slot.cycle])).sort((a, b) => a - b) : [];
  const highest = view.highestSlot ? view.highestSlotLabel || slotLabel(view.highestSlot) : '';
  const entrySlot = view.entrySlot ?? 1;
  return (
    <section className="panel onchain-card">
      <div className="panel-heading">
        <div><span className="eyebrow">ON-CHAIN · CONTRACT V2</span><h2><Blocks size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Matrix record of {view.bdId}</h2></div>
        <span className={`token-status-badge${view.placed ? '' : ' pending'}`}>{view.placed ? `Placed · slot ${highest || '—'}` : 'Not placed on-chain'}</span>
      </div>
      {!view.placed ? <p className="empty-copy">This ID has no on-chain placement yet (joined before Contract V2 and not replayed).</p> : null}
      <dl className="primary-token-grid">
        <div><dt>Sponsor</dt><dd>{view.sponsorBdId ?? '—'}</dd></div>
        <div><dt>Directs (on-chain)</dt><dd>{view.directCount}</dd></div>
        <div><dt>Highest slot</dt><dd>{highest || '—'}{view.entrySlot && view.entrySlot !== 1 ? ` (entered at Slot ${slotLabel(view.entrySlot)})` : ''}</dd></div>
        <div><dt>Payouts received</dt><dd>{btc(view.rewardTotals.netAtomic)}</dd></div>
        <div><dt>Position income</dt><dd>{btc(view.rewardTotals.positionNetAtomic)}</dd></div>
        <div><dt>Sponsor income</dt><dd>{btc(view.rewardTotals.sponsorNetAtomic)}</dd></div>
        <div><dt>Upgrade escrow held / released</dt><dd>{btc(view.escrow.heldAtomic)} / {btc(view.escrow.releasedAtomic)}</dd></div>
        <div><dt>Leaders Club (on-chain)</dt><dd>{view.club.club ? `Club ${view.club.club} since ${view.club.enteredAt ? formatDate(view.club.enteredAt) : ''}` : 'none yet (slot 4 opens C4)'}</dd></div>
        <div><dt>Club income received</dt><dd>{btc(view.club.receivedAtomic)} ({view.club.payouts.length} payouts)</dd></div>
      </dl>
      {view.club.payouts.length ? (
        <details className="onchain-section">
          <summary><Coins size={13} /> Leaders Club payouts ({view.club.payouts.length})</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>Settlement</th><th>Club</th><th>Amount (paid in full)</th><th>When</th><th>Tx</th></tr></thead><tbody>
            {view.club.payouts.map((row, index) => <tr key={index}><td>#{row.settlementId}</td><td>C{row.club}</td><td>{btc(row.amountAtomic)}</td><td>{formatDate(row.at)}</td><td>{tx(row.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}

      {slots.length ? (
        <div className="onchain-slots">
          <div className="onchain-slot-tabs">
            {slots.map((item) => (
              <button key={item.slot} type="button" className={`chip${openSlot === item.slot ? ' active' : ''}${item.virtual ? ' virtual' : ''}`} onClick={() => setOpenSlot(item.slot)} title={item.virtual ? `Slot ${slotLabel(item.slot)} is virtual: it seats lower-era IDs, was never paid for and never upgrades.` : undefined}>
                <Layers size={12} /> Slot {item.label ?? slotLabel(item.slot)}{item.virtual ? <span className="slot-virtual-tag">virtual</span> : null} · C{item.cycle} P{item.position}{item.upgradeWaiting ? ' · waiting 2 directs' : ''}{item.nextSlotOpened ? ' · next opened' : ''}
              </button>
            ))}
          </div>
          {slot ? (
            <div>
              <p className="empty-copy">
                Activated {formatDate(slot.activatedAt)}
                {slot.virtual
                  ? ' · virtual slot (seats lower-era IDs; no entry payment, no upgrade, cycle-2+ table)'
                  : slot.hostBdId
                    ? ` · seated under ${slot.hostBdId}${slot.levelsUp ? ` (${slot.levelsUp} level${slot.levelsUp === 1 ? '' : 's'} up)` : ''}`
                    : compareSlots(slot.slot, entrySlot) > 0 ? ' · entry seat went to Treasury' : ''}
                {' '}· current pointer C{slot.cycle} P{slot.position}
              </p>
              {cycles.map((cycle) => (
                <details key={cycle} open={cycle === slot.cycle && !compact}>
                  <summary>Cycle {cycle}{cycle === slot.cycle ? ' (running)' : ' (complete)'}</summary>
                  <div className="pt-table-wrap"><SeatTable seats={slot.seats} cycle={cycle} /></div>
                </details>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {view.seatsTaken.length ? (
        <details className="onchain-section">
          <summary><GitBranch size={13} /> Seats this ID took in other matrices ({view.seatsTaken.length})</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>Matrix owner</th><th>Slot</th><th>Cycle</th><th>Pos</th><th>Rule</th><th>Value</th><th>Tx</th></tr></thead><tbody>
            {view.seatsTaken.map((seat, index) => <tr key={index}><td>{seat.ownerBdId}</td><td>{slotLabel(seat.slot)}</td><td>{seat.cycle}</td><td>P{seat.position}</td><td>{kindLabel(seat.kind, seat.cycle)}</td><td>{btc(seat.amountAtomic)}</td><td>{tx(seat.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}

      {view.routing.length ? (
        <details className="onchain-section" open={showRouting} onToggle={(event) => setShowRouting((event.target as HTMLDetailsElement).open)}>
          <summary><GitBranch size={13} /> Routing decisions involving this ID ({view.routing.length}) — rule 9 §42 records</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>#</th><th>Type</th><th>Routed ID</th><th>From matrix</th><th>Slot</th><th>Seat</th><th>Checked</th><th>Candidates (✓ eligible)</th><th>Result</th><th>Tx</th></tr></thead><tbody>
            {view.routing.map((event) => (
              <tr key={event.routingEventId}>
                <td>{event.routingEventId}</td><td>{ROUTING_LABEL[event.routingType] ?? event.routingType}</td><td>{event.sourceBdId}</td><td>{event.slotOwnerBdId}</td><td>{slotLabel(event.slot)}</td>
                <td>C{event.sourceCycle} P{event.sourcePosition}</td><td>{event.candidatesChecked}</td>
                <td className="candidates">{event.candidates.map((candidate) => <span key={candidate.index} className={candidate.eligible ? 'ok' : 'no'}>{candidate.index}. {candidate.bdId}{candidate.eligible ? ' ✓' : ' ✗'}</span>)}</td>
                <td>{event.treasury ? 'Treasury' : `${event.selectedBdId} · C${event.destinationCycle} P${event.destinationPosition}`}</td><td>{tx(event.txHash)}</td>
              </tr>
            ))}
          </tbody></table></div>
        </details>
      ) : null}

      {view.rewards.length ? (
        <details className="onchain-section" open={!compact}>
          <summary><Coins size={13} /> Payouts credited by the vault ({view.rewards.length}) — credited in full (rule 26: no deductions; withdraw with the ID's wallet)</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>When</th><th>Reason</th><th>Amount</th><th>Wallet</th><th>Tx</th></tr></thead><tbody>
            {view.rewards.map((reward, index) => <tr key={index}><td>{formatDate(reward.at)}</td><td>{reward.reason === 'SPONSOR_INCOME' ? 'Referral income' : 'Slot payout'}</td><td><strong>{btc(reward.netAtomic)}</strong></td><td>{reward.wallet ? shortenAddress(reward.wallet, 5) : 'accrued (claimable)'}</td><td>{tx(reward.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}

      {view.sponsorIncomePaidOut.length ? (
        <details className="onchain-section">
          <summary><Coins size={13} /> Sponsor income this ID's joining paid upstream (10 levels, once)</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>Level</th><th>Recipient</th><th>Amount</th><th>Tx</th></tr></thead><tbody>
            {view.sponsorIncomePaidOut.map((row, index) => <tr key={index}><td>{row.level}</td><td>{row.recipientBdId ?? 'Treasury (no sponsor at this level)'}</td><td>{btc(row.amountAtomic)}</td><td>{tx(row.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}

      {view.escrow.events.length || view.claims.length ? (
        <details className="onchain-section">
          <summary><Lock size={13} /> Escrow & claims ledger</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>When</th><th>Event</th><th>Amount</th><th>Tx</th></tr></thead><tbody>
            {view.escrow.events.map((row, index) => <tr key={`e${index}`}><td>{formatDate(row.at)}</td><td>{row.kind === 'upgrade_held' ? 'Upgrade holding (P3/P4)' : 'Upgrade released → next slot'}</td><td>{btc(row.amountAtomic)}</td><td>{tx(row.txHash)}</td></tr>)}
            {view.claims.map((row, index) => <tr key={`c${index}`}><td>{formatDate(row.at)}</td><td>Claimed to {shortenAddress(row.wallet, 5)}</td><td>{btc(row.amountAtomic)}</td><td>{tx(row.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}
    </section>
  );
}

/*
 * Rule 26 (2026-09-27): PULL payouts. Rewards accrue to the ID's claimable ledger; the member withdraws 100% from its own
 * wallet with vault.claim(id) and pays the network gas itself. Shown in member, lookup and admin views alike; only the
 * wallet that owns the ID can execute (the contract rejects any other sender).
 */
export function ClaimButton({ bdId, idWallet, claimableAtomic, onDone }: { bdId: string; idWallet: string | null; claimableAtomic: string; onDone: () => void }) {
  const wallet = useWallet();
  const storageKey = `bdynamic.pendingClaim.${bdId}`;
  const readPending = (): { hash: string; amount: string; at: number } | null => {
    try { const raw = window.localStorage.getItem(storageKey); return raw ? JSON.parse(raw) as { hash: string; amount: string; at: number } : null; } catch { return null; }
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Pending confirmation" survives a page reload (owner 2026-10-01: never hide the button without feedback).
  const [pending, setPending] = useState<{ hash: string; amount: string; at: number } | null>(() => readPending());
  const [confirmed, setConfirmed] = useState<string | null>(null);
  const account = wallet.address as Address | null;
  const isOwnerWallet = Boolean(account && idWallet && account.toLowerCase() === idWallet.toLowerCase());
  const amount = BigInt(claimableAtomic || '0');

  // The withdrawal is confirmed once the indexed claimable balance has dropped below what was pending.
  useEffect(() => {
    if (!pending) return;
    if (amount < BigInt(pending.amount)) {
      setConfirmed(pending.hash);
      setPending(null);
      try { window.localStorage.removeItem(storageKey); } catch { /* storage unavailable */ }
      return;
    }
    if (Date.now() - pending.at > 10 * 60_000) { // gave up waiting: the transaction probably failed or was dropped
      setPending(null);
      try { window.localStorage.removeItem(storageKey); } catch { /* storage unavailable */ }
      return;
    }
    const timer = window.setInterval(onDone, 10_000);
    return () => window.clearInterval(timer);
  }, [pending, amount, onDone, storageKey]);

  if (pending) {
    return <small className="empty-copy"> · <strong>Withdrawal pending confirmation…</strong> {btc(pending.amount)} sent {tx(pending.hash)} - waiting for the block to finalize (about 15-60 seconds). The button is locked meanwhile so the same amount cannot be requested twice.</small>;
  }
  if (amount <= 0n) return confirmed ? <small className="empty-copy"> · Withdrawal confirmed ✔ {tx(confirmed)} - the BTCB is in your wallet (press "Add BTCB to wallet" if the wallet app does not list it).</small> : null;
  if (!isOwnerWallet) return <small className="empty-copy"> · connect the wallet of {bdId} to withdraw (gas paid by that wallet, 0% fee)</small>;
  const numericId = BigInt(bdId.replace(/^BD-/i, ''));
  const run = async () => {
    setBusy(true); setError(null); setConfirmed(null);
    try {
      const hash = await sendGovernanceCall(resolveGovernanceAddresses(), account!, 'treasury', VAULT_ABI, 'claim', [numericId]);
      const record = { hash: String(hash), amount: amount.toString(), at: Date.now() };
      try { window.localStorage.setItem(storageKey, JSON.stringify(record)); } catch { /* storage unavailable */ }
      setPending(record);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Withdrawal failed.');
    } finally {
      setBusy(false);
    }
  };
  return <span> <button className="button primary small" type="button" disabled={busy} onClick={() => void run()}>{busy ? 'Confirm in wallet…' : 'Withdraw to my wallet'}</button>{confirmed ? <small className="empty-copy"> Previous withdrawal confirmed ✔ {tx(confirmed)}</small> : null}{error ? <small className="token-identity-error"> {error}</small> : null}</span>;
}

/*
 * Member income & withdrawal card (owner 2026-09-30: "total income is not showing anywhere / where is the withdraw
 * option"). On-chain figures of one ID: total income, its parts, what was already withdrawn, what can be withdrawn now,
 * the Withdraw button (rule 26: the ID's wallet pays the gas, 0% deduction) and the ID-wise Leaders Club payouts.
 * Used on the member Overview; the same data is in Lookup and the admin Member explorer through OnChainMatrixSection.
 */
export function MemberEarningsCard({ bdId }: { bdId: string }) {
  const [data, setData] = useState<{ view: OnChainMemberView | null; ledger: MemberLedger | null } | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api<{ view: OnChainMemberView | null; ledger: MemberLedger | null }>(`/api/public/onchain/${encodeURIComponent(bdId)}`)
      .then((result) => { if (!cancelled) setData(result); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [bdId, refresh]);
  const walletState = useWallet();
  const [walletBtcb, setWalletBtcb] = useState<string | null>(null);
  const idWallet = data?.view?.wallet ?? null;
  useEffect(() => {
    let cancelled = false;
    const token = import.meta.env.VITE_PAYMENT_TOKEN_ADDRESS as string | undefined;
    if (!idWallet || !token) { setWalletBtcb(null); return; }
    const rpcUrl = (import.meta.env.VITE_RPC_PUBLIC_URL as string | undefined)?.trim() || (Number(import.meta.env.VITE_CHAIN_ID ?? 97) === 56 ? 'https://bsc-dataseed.bnbchain.org' : 'https://data-seed-prebsc-1-s1.bnbchain.org:8545');
    const data = `0x70a08231${idWallet.toLowerCase().replace(/^0x/, '').padStart(64, '0')}`;
    fetch(rpcUrl, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'eth_call', params: [{ to: token, data }, 'latest'] }) })
      .then((response) => response.json())
      .then((body: { result?: string }) => { if (!cancelled && body.result && body.result !== '0x') setWalletBtcb(BigInt(body.result).toString()); })
      .catch(() => { if (!cancelled) setWalletBtcb(null); });
    return () => { cancelled = true; };
  }, [idWallet, refresh]);
  async function addBtcbToWallet() {
    const token = import.meta.env.VITE_PAYMENT_TOKEN_ADDRESS as string | undefined;
    const provider = (window as unknown as { ethereum?: { request: (args: { method: string; params?: unknown }) => Promise<unknown> } }).ethereum;
    if (!token || !provider) return;
    try { await provider.request({ method: 'wallet_watchAsset', params: { type: 'ERC20', options: { address: token, symbol: 'BTCB', decimals: 18 } } }); } catch { /* user dismissed */ }
  }
  if (!data?.view) return null;
  const { view, ledger } = data;
  const lastClaim = view.claims.length ? view.claims[view.claims.length - 1]! : null;
  const withdrawn = view.claims.reduce((sum, row) => sum + BigInt(row.amountAtomic), 0n).toString();
  const total = ledger?.cumulativeEarningsAtomic ?? view.rewardTotals.netAtomic;
  const claimable = ledger?.claimableAtomic ?? '0';
  return (
    <section className="panel onchain-ledger" id="member-income">
      <div className="panel-heading"><div><span className="eyebrow">MY INCOME &amp; WITHDRAWAL · ON-CHAIN</span><h2>Income of {bdId}</h2></div></div>
      <dl className="primary-token-grid">
        <div><dt>Total income</dt><dd><strong>{btc(total)}</strong></dd></div>
        <div><dt>Sponsor income</dt><dd>{btc(view.rewardTotals.sponsorNetAtomic)}</dd></div>
        <div><dt>Slot / position income</dt><dd>{btc(view.rewardTotals.positionNetAtomic)}</dd></div>
        <div><dt>Leaders Club income</dt><dd>{btc(view.club.receivedAtomic)}{view.club.club ? ` · Club ${view.club.club}` : ''}</dd></div>
        <div><dt>Already withdrawn</dt><dd>{btc(withdrawn)} ({view.claims.length})</dd></div>
        <div><dt>Available to withdraw</dt><dd><strong>{btc(claimable)}</strong><ClaimButton bdId={bdId} idWallet={view.wallet ?? null} claimableAtomic={claimable} onDone={() => setRefresh((value) => value + 1)} /></dd></div>
        <div><dt>BTCB in this ID's wallet (on-chain)</dt><dd>{walletBtcb === null ? '—' : btc(walletBtcb)}{walletState.address && idWallet && walletState.address.toLowerCase() === idWallet.toLowerCase() ? <> <button className="button ghost small" type="button" onClick={() => void addBtcbToWallet()}>Add BTCB to wallet</button></> : null}</dd></div>
      </dl>
      {lastClaim ? <p className="empty-copy">Last withdrawal: <strong>{btc(lastClaim.amountAtomic)}</strong> on {formatDate(lastClaim.at)} to {shortenAddress(lastClaim.wallet, 5)} · {tx(lastClaim.txHash)} · confirmed on-chain ✔. "Total income" is the lifetime figure and never goes down; a withdrawal moves the amount from "Available to withdraw" to "Already withdrawn". If the wallet app does not list BTCB, press "Add BTCB to wallet" (token {String(import.meta.env.VITE_PAYMENT_TOKEN_ADDRESS ?? '')}); the tBNB shown in the wallet is only the gas balance.</p> : null}
      <p className="empty-copy">No deduction on any payout. Press Withdraw with the wallet of {bdId} connected: 100% goes to that wallet, you pay only the network gas (BNB).{BigInt(claimable || '0') === 0n ? ' Nothing to withdraw right now.' : ''}</p>
      {view.club.payouts.length ? (
        <details open>
          <summary>Leaders Club payouts received ({view.club.payouts.length})</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>Settlement</th><th>Club</th><th>Amount</th><th>When</th><th>Tx</th></tr></thead><tbody>
            {view.club.payouts.map((row, index) => <tr key={index}><td>#{row.settlementId}</td><td>Club {row.club}</td><td>{btc(row.amountAtomic)}</td><td>{formatDate(row.at)}</td><td>{tx(row.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}
      {view.claims.length ? (
        <details>
          <summary>Withdrawal history ({view.claims.length})</summary>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>When</th><th>To wallet</th><th>Amount</th><th>Tx</th></tr></thead><tbody>
            {view.claims.map((row, index) => <tr key={index}><td>{formatDate(row.at)}</td><td>{shortenAddress(row.wallet, 5)}</td><td>{btc(row.amountAtomic)}</td><td>{tx(row.txHash)}</td></tr>)}
          </tbody></table></div>
        </details>
      ) : null}
    </section>
  );
}
